import React, { useEffect } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { setAlert, clearAlert } from '../../actions/alert'
import { ALERT_INFO } from '../Alert'
import PrivateTemplate from '../PrivateTemplate'
import IdleTimer from '../IdleTimer/IdleTimer'
import AccessGuard from '../AccessGuard'

/**
 * @param {React.ReactNode} children - One or more React components to be
 * rendered if the user is authenticated
 * @param {string} title Page title passed in to PrivateTemplate
 * which is automatically passed via withRouter
 */
function PrivateRoute({
  children,
  title,
  subtitle,
  requiredPermissions,
  requiredFeatureFlags,
  requiresApproval,
}) {
  const authenticated = useSelector((state) => state.auth.authenticated)
  const authLoading = useSelector((state) => state.auth.loading)

  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()

  useEffect(() => {
    if (!authenticated && !authLoading) {
      const next = new URLSearchParams({
        next: `${location.pathname}${location.search}${location.hash}`,
      })
      dispatch(setAlert({ heading: 'Please sign in first', type: ALERT_INFO }))
      navigate(`/?${next}`, { replace: true })
    }

    if (authLoading) {
      dispatch(setAlert({ heading: 'Please wait...', type: ALERT_INFO }))
    }

    if (authenticated) {
      dispatch(clearAlert())
    }
  }, [authenticated, authLoading, dispatch, navigate, location])

  if (authenticated) {
    return (
      <AccessGuard
        requiresApproval={requiresApproval}
        requiredPermissions={requiredPermissions}
        requiredFeatureFlags={requiredFeatureFlags}
        notAllowedComponent={<Navigate to="/home" />}
      >
        <PrivateTemplate title={title} subtitle={subtitle}>
          {children}
          <IdleTimer />
        </PrivateTemplate>
      </AccessGuard>
    )
  }

  return null
}

export default PrivateRoute
